import React, { useEffect, useState } from 'react'
import { Button, Col, Form as BootstrapForm, Modal, Row } from 'react-bootstrap'
import { useRouter } from '@tanstack/react-router'
import Icon from '@mdi/react'
import { mdiAlertBoxOutline, mdiCheckboxMarkedCircle, mdiMagnify, mdiTimerSand } from '@mdi/js'
import { Formik } from 'formik'
import * as yup from 'yup'
import SurveyUpdateModalPermField from './SurveyUpdateModalPermField'
import { api } from '../shared/ApiProvider'

const formatDateForAPI = (dateString) => {
  if (!dateString) return null
  const [year, month, day] = dateString.split('-')
  return `${month}/${day}/${year}`
}

// Simple validation schema for the update modal
const surveyUpdateSchema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  middleName: yup.string().notRequired(),
  lastName: yup.string().required('Last name is required'),
  birthDate: yup.date().required('Birth date is required').nullable(),
  sex: yup
    .mixed()
    .oneOf(['M', 'F'], 'Please specify gender')
    .required('Gender is required'),
})

// This Modal appears ONLY when there is no PERM number attached to the selected row in the SurveyList component.

export default function SurveyUpdateModal({
                                            showSurveyUpdateModal,
                                            toggleModal,
                                            surveyInitialVals,
                                          }) {
  const {
    firstName,
    lastName,
    birthDate,
    sex,
    id,
    permNumber,
    permNumberVerified,
  } = surveyInitialVals
  const [modalState, setModalState] = useState('inactive')
  const [modalMessage, setModalMessage] = useState('No Matching PERM Number')
  const [matchedStudent, setMatchedStudent] = useState(null)
  const [loadingStudent, setLoadingStudent] = useState(false)
  const router = useRouter()

  // reset modal state each time it opens
  useEffect(() => {
    if (showSurveyUpdateModal) {
      setModalState('inactive')
      setModalMessage('No Matching PERM Number')
      setMatchedStudent(null)

      console.log('Modal opened with surveyInitialVals:', surveyInitialVals)
      console.log(
        'permNumber:',
        permNumber,
        'permNumberVerified:',
        permNumberVerified,
      )

      // If there's an unverified permNumber, look up the student master record
      // Check for null/undefined but allow 0 as a valid perm number
      if (permNumber != null && permNumberVerified !== true) {
        console.log('Looking up student master for perm:', permNumber)
        lookupStudentMaster(permNumber)
      }
    }
  }, [showSurveyUpdateModal, permNumber, permNumberVerified])

  const lookupStudentMaster = async (perm) => {
    setLoadingStudent(true)
    console.log('Fetching survey and student data for survey ID:', id)
    try {
      // Use the surveyandstudent endpoint which returns both survey and matched student
      const response = await api.get(`/surveyandstudent/${id}`)
      console.log('Survey and student response:', response.data)

      // Check if we got a matched student from the student master
      if (response.data && response.data.matchedStudent) {
        setMatchedStudent(response.data.matchedStudent)
        setModalMessage(
          'Unverified PERM Number - Student Found in Master Database',
        )
        console.log('Matched student set:', response.data.matchedStudent)
      } else {
        console.log(
          'No matched student found - user will need to search manually',
        )
        setModalMessage('No student master record found for this PERM number')
      }
    } catch (error) {
      console.error('Error looking up student data:', error)
      setModalMessage('Error looking up student data')
    } finally {
      setLoadingStudent(false)
    }
  }

  const acceptPermNumber = async () => {
    setModalState('searching')
    setModalMessage('Accepting PERM Number...')

    console.log('About to accept permNumber:', matchedStudent.permNumber)
    console.log('Survey ID:', id)
    console.log('Full surveyInitialVals:', surveyInitialVals)

    try {
      const result = await api.put(`/survey/${id}`, {
        permnumber: matchedStudent.permNumber,
      })
      console.log('Accept perm number response:', result)
      if (result.status === 200 || result.status === 303) {
        setModalState('success')
        setModalMessage('PERM Number accepted and verified')
        setTimeout(() => {
          router.navigate({ to: `/survey/${id}` })
        }, 1000)
      }
    } catch (error) {
      console.error('Error accepting perm number:', error)
      setModalMessage('Error accepting PERM number. Please try again.')
      setModalState('failure')
    }
  }

  const submitSurveyUpdate = async (data, { resetForm }) => {
    setModalState('searching')
    setModalMessage('Searching for student...')
    // Search for student in student master using the provided demographics
    // Map frontend field names to Azure Function API field names
    const searchParams = {
      fname: data.firstName,
      lname: data.lastName,
      dob: formatDateForAPI(data.birthDate),
      gender: data.sex,
    }

    console.log('Searching for student with params:', searchParams)

    api.post('/student/lookup?byName', searchParams)
      .then(({ data }) => {
        if (data.permNumber) {
          setMatchedStudent(data)
          setModalMessage('Student Found in Master Database')
          setModalState('inactive')
          console.log('Matched student found:', data)
        } else {
          setModalMessage('No matching student found. Try different search criteria.')
          setModalState('failure')
          setMatchedStudent(null)
        }
      })
      .catch((error) => {
        console.error('Error searching for student:', error)
        if (error.response?.status === 404) {
          setModalMessage('No Student Found. Try different search criteria or use manual PERM entry.')
          setMatchedStudent(null)
        } else {
          setModalMessage('An error occurred during search. Please try later.')
        }
        setModalState('failure')
      })
  }

  const determineIcon = () => {
    switch (modalState) {
      case 'inactive':
        return <Icon path={mdiAlertBoxOutline} color="red" size={1.5} />
      case 'searching':
        return (
          <Icon
            path={mdiTimerSand}
            size={1.5}
            color="blue"
            className="icon-vertical-center"
          />
        )
      case 'success':
        return (
          <Icon
            path={mdiCheckboxMarkedCircle}
            size={1.5}
            color="green"
            className="icon-vertical-center"
          />
        )
      case 'failure':
        return (
          <Icon
            path={mdiAlertBoxOutline}
            size={1.5}
            color="red"
            className="icon-vertical-center"
          />
        )
      default:
        return <Icon path={mdiAlertBoxOutline} color="red" size={1.5} />
    }
  }

  return (
    <Modal show={showSurveyUpdateModal} onHide={toggleModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3">
          {determineIcon()}
          <span style={{ paddingLeft: '12px' }}>{modalMessage}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="container-fluid">
          <Row>
            {/* Row 1 */}
            <Col md={6}>
              {/* Student info search form */}
              <Formik
                validationSchema={surveyUpdateSchema}
                onSubmit={submitSurveyUpdate}
                initialValues={{
                  firstName: firstName || '',
                  middleName: '',
                  lastName: lastName || '',
                  birthDate: birthDate || '',
                  sex: sex || '',
                }}
              >
                {({ handleSubmit, handleChange, values, errors, touched }) => (
                  <BootstrapForm onSubmit={handleSubmit} noValidate>
                    <Row>
                      <BootstrapForm.Group
                        as={Col}
                        md="6"
                        controlId="firstName"
                      >
                        <BootstrapForm.Label>First name</BootstrapForm.Label>
                        <BootstrapForm.Control
                          type="text"
                          name="firstName"
                          value={values.firstName}
                          onChange={handleChange}
                          isInvalid={touched.firstName && !!errors.firstName}
                        />
                        <BootstrapForm.Control.Feedback type="invalid">
                          {errors.firstName || '*required'}
                        </BootstrapForm.Control.Feedback>
                      </BootstrapForm.Group>
                      <BootstrapForm.Group as={Col} md="6" controlId="lastName">
                        <BootstrapForm.Label>Last name</BootstrapForm.Label>
                        <BootstrapForm.Control
                          type="text"
                          name="lastName"
                          value={values.lastName}
                          onChange={handleChange}
                          isInvalid={touched.lastName && !!errors.lastName}
                        />
                        <BootstrapForm.Control.Feedback type="invalid">
                          {errors.lastName || '*required'}
                        </BootstrapForm.Control.Feedback>
                      </BootstrapForm.Group>
                    </Row>
                    <Row>
                      <BootstrapForm.Group
                        as={Col}
                        md="6"
                        controlId="birthDate"
                      >
                        <BootstrapForm.Label>DOB</BootstrapForm.Label>
                        <BootstrapForm.Control
                          type="date"
                          name="birthDate"
                          value={values.birthDate}
                          onChange={handleChange}
                          isInvalid={touched.birthDate && !!errors.birthDate}
                        />
                        <BootstrapForm.Control.Feedback type="invalid">
                          {errors.birthDate || '*required'}
                        </BootstrapForm.Control.Feedback>
                      </BootstrapForm.Group>
                      <BootstrapForm.Group as={Col} md="6" controlId="sex">
                        <BootstrapForm.Label>Sex</BootstrapForm.Label>
                        <div
                          className="d-flex flex-row align-items-center justify-content-center"
                          style={{ gap: '2rem', minHeight: '48px' }}
                        >
                          <BootstrapForm.Check
                            type="radio"
                            label="Male"
                            name="sex"
                            value="M"
                            checked={values.sex === 'M'}
                            onChange={handleChange}
                            className="me-2"
                            isInvalid={touched.sex && !!errors.sex}
                          />
                          <BootstrapForm.Check
                            type="radio"
                            label="Female"
                            name="sex"
                            value="F"
                            checked={values.sex === 'F'}
                            onChange={handleChange}
                            className="me-2"
                            isInvalid={touched.sex && !!errors.sex}
                          />
                        </div>
                        {touched.sex && errors.sex && (
                          <div className="invalid-feedback d-block">
                            {errors.sex}
                          </div>
                        )}
                      </BootstrapForm.Group>
                    </Row>
                    <Button
                      type="submit"
                      disabled={modalState === 'searching'}
                      className="mt-2"
                    >
                      <Icon
                        path={mdiMagnify}
                        className="me-2"
                        color="white"
                        size={1}
                      />
                      {modalState === 'searching' ? 'Searching...' : 'Search'}
                    </Button>
                  </BootstrapForm>
                )}
              </Formik>
            </Col>
            <Col md={6}>
              {/* Perm number search form */}
              <SurveyUpdateModalPermField
                id={id}
                screenerName={surveyInitialVals.screenerName}
              />
            </Col>
          </Row>
          <Row className="mt-4">
            {/* Row 2 */}
            <Col md={6}>
              {/* Search results and button */}
              {loadingStudent ? (
                <div className="d-flex justify-content-center align-items-center p-4">
                  <Icon path={mdiTimerSand} spin size={2} color="blue" />
                  <span className="ms-3">
                    Looking up student master record...
                  </span>
                </div>
              ) : matchedStudent ? (
                <div className="mb-4 p-3 border rounded bg-light">
                  <h5 className="text-primary mb-3">
                    <strong>📋 Student Master Record Found</strong>
                  </h5>
                  <div>
                    <strong>PERM Number:</strong>{' '}
                    <span className="text-monospace">
                      {matchedStudent.permNumber}
                    </span>
                    <br />
                    <strong>Name:</strong> {matchedStudent.firstName}{' '}
                    {matchedStudent.middleName || ''} {matchedStudent.lastName}{' '}
                    {matchedStudent.postName || ''}
                    <br />
                    <strong>Former Last Name:</strong>{' '}
                    {matchedStudent.formerLastName || 'N/A'}
                    <br />
                    <strong>Date of Birth:</strong> {matchedStudent.dateOfBirth}
                    <br />
                    <strong>Gender:</strong> {matchedStudent.gender}
                  </div>
                  <div className="mt-3 p-3 bg-warning-subtle border border-warning rounded">
                    <p className="mb-2">
                      <strong>⚠️ This PERM number needs verification</strong>
                    </p>
                    <p className="mb-0">
                      Click "Accept PERM Number" below to verify this PERM
                      number and proceed to the side-by-side comparison.
                    </p>
                  </div>
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant="success"
                      onClick={acceptPermNumber}
                      disabled={modalState === 'searching'}
                    >
                      <Icon
                        path={mdiCheckboxMarkedCircle}
                        className="me-2"
                        color="white"
                        size={1}
                      />
                      {modalState === 'searching'
                        ? 'Accepting...'
                        : 'Accept PERM Number'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-3 border rounded bg-light">
                  <strong>
                    No student master record found for this search.
                  </strong>
                </div>
              )}
            </Col>
            <Col md={6}>
              {/* Screener info - only here */}
              <div className="mb-4 p-3 border rounded bg-light">
                <h5 className="text-primary mb-3">
                  <strong>Screener Contact Information</strong>
                </h5>
                {surveyInitialVals.screenerName &&
                surveyInitialVals.screenerName.includes('@')
                  ? (() => {
                    const words = surveyInitialVals.screenerName.split(' ')
                    const emailIdx = words.findIndex((w) => w.includes('@'))
                    if (emailIdx !== -1) {
                      const email = words[emailIdx]
                      const name = words
                        .filter((_, idx) => idx !== emailIdx)
                        .join(' ')
                      return (
                        <>
                          <div>
                            <b>Name:</b>
                          </div>
                          <div>{name.trim()}</div>
                          <div className="mt-2">
                            <b>Email:</b>
                          </div>
                          <div>{email}</div>
                        </>
                      )
                    } else {
                      return (
                        <>
                          <div>
                            <b>Name:</b>
                          </div>
                          <div>{surveyInitialVals.screenerName}</div>
                        </>
                      )
                    }
                  })()
                  : surveyInitialVals.screenerName && (
                  <>
                    <div>
                      <b>Name:</b>
                    </div>
                    <div>{surveyInitialVals.screenerName}</div>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Modal.Body>
    </Modal>
  )
}
